import { useEffect, useState } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Paper, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import type { ChildCollectionConfig } from '../../lib/types';
import type { UploadedFile } from '../../lib/upload';
import ChildRow, { type ChildItem } from './ChildRow';
import ChildDropzone from './ChildDropzone';
import YoutubeUrlForm from './YoutubeUrlForm';

interface ChildCollectionProps {
  config: ChildCollectionConfig;
  parentId: number;
}

export default function ChildCollection({ config, parentId }: ChildCollectionProps) {
  const { entity, foreignKey, kind } = config;
  const utils = trpc.useUtils();
  const listQuery = trpc.admin.crud.list.useQuery({
    entity, page: 1, pageSize: 200, sortBy: 'sort', sortDir: 'asc', filter: { [foreignKey]: parentId },
  });
  const [items, setItems] = useState<ChildItem[]>([]);

  useEffect(() => {
    setItems((listQuery.data?.rows ?? []) as unknown as ChildItem[]);
  }, [listQuery.data]);

  const onError = (err: { message: string }) => notifications.show({ color: 'red', message: err.message });
  const refresh = () => utils.admin.crud.list.invalidate({ entity });
  const createMutation = trpc.admin.crud.create.useMutation({ onError });
  const updateMutation = trpc.admin.crud.update.useMutation({ onSuccess: refresh, onError });
  const deleteMutation = trpc.admin.crud.delete.useMutation({ onSuccess: refresh, onError });
  const reorderMutation = trpc.admin.crud.reorder.useMutation({ onError });
  const sensors = useSensors(useSensor(PointerSensor));

  async function createRows(rows: Record<string, unknown>[]) {
    const nextSort = Math.max(0, ...items.map((item) => item.sort ?? 0)) + 1;
    await Promise.all(
      rows.map((row, index) =>
        createMutation.mutateAsync({ entity, data: { ...row, [foreignKey]: parentId, sort: nextSort + index } }),
      ),
    ).finally(refresh);
  }

  function handleUploaded(files: UploadedFile[]) {
    return createRows(files.map((file) => ({ file: file.path, title: kind === 'file' ? stripExtension(file.originalName) : null })));
  }

  function handleDelete(item: ChildItem) {
    modals.openConfirmModal({
      title: `Delete from ${config.title}`,
      children: <Text size="sm">Remove this item from the product?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteMutation.mutate({ entity, id: item.id }),
    });
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((item) => String(item.id) === String(active.id));
    const newIndex = items.findIndex((item) => String(item.id) === String(over.id));
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    reorderMutation.mutate({ entity, items: reordered.map((item, index) => ({ id: item.id, sort: index + 1 })) });
  }

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Title order={4}>{config.title}</Title>
        {items.length === 0 && !listQuery.isLoading && <Text size="sm" c="dimmed">Nothing added yet.</Text>}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((item) => String(item.id))} strategy={verticalListSortingStrategy}>
            {items.map((item) => (
              <ChildRow
                key={item.id}
                item={item}
                kind={kind}
                onUpdate={(data) => updateMutation.mutate({ entity, id: item.id, data })}
                onDelete={() => handleDelete(item)}
              />
            ))}
          </SortableContext>
        </DndContext>
        {kind === 'youtube'
          ? <YoutubeUrlForm onAdd={(url) => createRows([{ url }])} />
          : <ChildDropzone kind={kind} onUploaded={handleUploaded} />}
      </Stack>
    </Paper>
  );
}

function stripExtension(filename: string) {
  return filename.replace(/\.[^.]+$/, '');
}
